/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask, restartableTask } from 'ember-concurrency';
import { importSync } from '@embroider/macros';
import { ComponentLike } from '@glint/template';
import getPreviewVariables from 'cio/utils/get-preview-variables';

import DS from 'ember-data';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import EmailPreviewService from 'cio/services/email-preview';
import CustomerModel from 'cio/models/customer';
import { IdentifierKey } from 'cio/utils/identifiers';
import { ComposerNewsletterTemplate } from 'cio/utils/composer/composer-editable-entity';
import ObjectModel from 'cio/models/object';
import getObjectPreviewVariables from 'cio/utils/object-preview-variables';
import { Editor } from 'cio/services/editor';

interface NewsletterOverviewContentPreviewArgs {
  editableEntity: ComposerNewsletterTemplate;
  onSelectTemplate: (templateId: string) => unknown;
}

export default class NewsletterOverviewContentPreview extends Component<NewsletterOverviewContentPreviewArgs> {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service emailPreview!: EmailPreviewService;
  @service store!: DS.Store;

  @tracked mergeCustomer?: CustomerModel;
  @tracked searchlessRecipientOptions: { value: CustomerModel; label: string; model: CustomerModel }[] = [];

  objectPreviewVariables: Record<string, ObjectModel['attributes'][]> = {};

  get templatePreviewRequestData() {
    let { template } = this.args.editableEntity;
    let isJsonEditor = template.editor === Editor.JSON;
    let isVisualEditor = template.editor === Editor.VISUAL;

    switch (template.templateType) {
      case 'push':
        return {
          body: isJsonEditor ? template.bodyJson : template.body,
          has_json: isJsonEditor,
          title: template.subject,
          to: template.recipient,
        };
      case 'in_app':
        return {
          body_json: template.bodyJson,
          body_html: isVisualEditor ? template.body : null,
          to: template.recipient,
          editor: template.editor,
        };
      case 'twilio':
        return {
          body: template.body,
          from: template.fromIdentity.phone,
          to: template.recipient,
          image_url: template.imageUrl,
        };
      case 'webhook':
        return {
          body: template.body,
          headers: template.headers,
          url: template.url,
        };
      default:
        return {};
    }
  }

  get templateItems() {
    return this.args.editableEntity.newsletter.activeTemplatesSelectItems;
  }

  get templatePreviewComponent() {
    let module = importSync(`./${this.args.editableEntity.template.templateType.replace('_', '-')}`) as {
      default: ComponentLike<any>;
    };

    return module.default;
  }

  getEmailPreview() {
    return this.emailPreview.generate(this.args.editableEntity.template, {
      customer: this.mergeCustomer?.attrs ?? {},
      templateVariables: this.fetchTemplateVariables.last?.value,
      objects: this.objectPreviewVariables,
    });
  }

  getTemplatePreview() {
    let { template } = this.args.editableEntity;

    return this.api.post(`previews/${template.templateType}`, {
      data: {
        ...this.templatePreviewRequestData,
        variables: getPreviewVariables({
          customer: this.mergeCustomer?.attrs ?? {},
          templateVariables: this.fetchTemplateVariables.last?.value,
          objects: this.objectPreviewVariables,
        } as any),
      },
    });
  }

  fetchPreviewTask = restartableTask(async () => {
    await this.loadObjectPreviewVariables.perform();

    if (this.args.editableEntity.template.templateType === 'email') {
      return await this.getEmailPreview();
    } else {
      return await this.getTemplatePreview();
    }
  });

  @action
  async getPeopleOptions(term?: string) {
    let people = await this.store.query('customer', { email: term && `${term}*` });

    return people.map((person: CustomerModel) => {
      return {
        label: person.email ?? `Person with id ${person.identifiers.id}`,
        value: person,
        model: person,
      };
    });
  }

  sendToRecipientTask = dropTask(async () => {
    if (!this.mergeCustomer) {
      return;
    }

    try {
      await this.api.post(`newsletters/${this.args.editableEntity.newsletter.id}/forward`, {
        data: {
          internal_id: this.mergeCustomer.identifiers[IdentifierKey.CioId],
          template_id: this.args.editableEntity.template.id,
        },
      });

      this.alerts.success('newsletter.test', 'Newsletter sent');
    } catch {
      this.alerts.error('newsletter.test', 'Newsletter unable to send.');
    }
  });

  fetchTemplateVariables = dropTask(async () => {
    let { variables } = (await this.args.editableEntity.template?.fetchPreviewVariables({})) ?? {};

    return variables;
  });

  @action
  selectTemplate(templateId: string) {
    this.args.onSelectTemplate(templateId);
    this.fetchPreviewTask.perform();
  }

  @action
  selectCustomer(option: { value: CustomerModel }) {
    this.mergeCustomer = option?.value;
    this.fetchPreviewTask.perform();
  }

  loadObjectPreviewVariables = restartableTask(async () => {
    let { objects } = (await this.mergeCustomer?.getRelatedObjectsPerType()) ?? {};
    this.objectPreviewVariables = getObjectPreviewVariables(objects, this.mergeCustomer?.id);
  });

  constructor(owner: unknown, args: NewsletterOverviewContentPreviewArgs) {
    super(owner, args);
    this.getPeopleOptions().then((customers) => {
      this.searchlessRecipientOptions = customers;
    });

    this.fetchTemplateVariables.perform();
    this.fetchPreviewTask.perform();
  }
}
